import { useCallback } from "react";
import posthog from "posthog-js";
import * as Sentry from "@sentry/react";
import { isPosthogReady } from "./init";
import type { AnalyticsUser } from "./types";

export function useAnalytics() {
  const track = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      if (isPosthogReady()) posthog.capture(event, properties);
      Sentry.addBreadcrumb({
        category: "analytics",
        message: event,
        data: properties,
      });
    },
    [],
  );

  const identify = useCallback((user: AnalyticsUser) => {
    if (isPosthogReady()) {
      posthog.identify(user.id, {
        email: user.email,
        name: user.name,
        tenant_id: user.tenantId,
        tenant_name: user.tenantName,
      });

      if (user.tenantId) {
        posthog.group("tenant", user.tenantId, { name: user.tenantName });
      }
    }

    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name,
    });

    if (user.tenantId) {
      Sentry.setTag("tenant_id", user.tenantId);
    }
  }, []);

  const reset = useCallback(() => {
    if (isPosthogReady()) posthog.reset();
    Sentry.setUser(null);
  }, []);

  return { track, identify, reset };
}
